import { BRAND } from '@verlo/shared/lib/brand';
/**
 * Trend Chart — 8-period dual-line revenue + EBITDA mini-chart with SVG.
 */
const fmtShort = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(1)}k`;
  return `£${Math.round(n)}`;
};

const TrendChart = ({ trend }) => {
  if (!trend?.length) return null;
  const W = 720;
  const H = 220;
  const padL = 40;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const revs = trend.map((t) => t.revenue);
  const ebs = trend.map((t) => t.ebitda);
  const max = Math.max(...revs);
  const min = 0;
  const range = max - min || 1;

  const xFor = (i) => padL + (innerW / (trend.length - 1 || 1)) * i;
  const yFor = (v) => padT + (1 - (v - min) / range) * innerH;

  const path = (vals, color) => {
    const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
    return <path d={d} stroke={color} strokeWidth="2" fill="none" />;
  };

  const area = (vals, color) => {
    const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
    const close = ` L ${xFor(vals.length - 1)} ${yFor(0)} L ${xFor(0)} ${yFor(0)} Z`;
    return <path d={d + close} fill={color} opacity="0.08" />;
  };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full" data-testid="trend-chart-svg">
      {/* Y gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((p) => {
        const y = padT + p * innerH;
        const val = max - p * range;
        return (
          <g key={p}>
            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#f1f5f9" />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8" fontFamily="Manrope, sans-serif">
              {fmtShort(val)}
            </text>
          </g>
        );
      })}
      {/* Revenue area */}
      {area(revs, BRAND.verlo)}
      {path(revs, BRAND.verlo)}
      {/* EBITDA line */}
      {path(ebs, '#10b981')}
      {/* Points */}
      {trend.map((_, i) => (
        <g key={i}>
          <circle cx={xFor(i)} cy={yFor(revs[i])} r="2.5" fill={BRAND.verlo} />
          <circle cx={xFor(i)} cy={yFor(ebs[i])} r="2.5" fill="#10b981" />
          <text x={xFor(i)} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#94a3b8">
            {trend[i].label.split(' ').slice(0, 2).join(' ')}
          </text>
        </g>
      ))}
      {/* Legend */}
      <g transform={`translate(${padL}, ${padT - 8})`}>
        <circle r="3" fill={BRAND.verlo} cx={5} cy={0} />
        <text x={12} y={3} fontSize="10" fill="#475569" fontWeight="600">Revenue</text>
        <circle r="3" fill="#10b981" cx={75} cy={0} />
        <text x={82} y={3} fontSize="10" fill="#475569" fontWeight="600">EBITDA</text>
      </g>
    </svg>
  );
};

export default TrendChart;
